import React, { useEffect, useRef, useState } from "react";
import { Btn, Eyebrow } from "../components/Primitives";

/**
 * Captures a single photo via getUserMedia. Returns a Blob via onCapture(blob).
 */
export default function LivePhotoCapture({ onCapture, onCancel, existingUrl }) {
    const videoRef = useRef(null);
    const canvasRef = useRef(null);
    const streamRef = useRef(null);
    const [ready, setReady] = useState(false);
    const [error, setError] = useState("");
    const [previewUrl, setPreviewUrl] = useState(existingUrl || null);
    const [capturedBlob, setCapturedBlob] = useState(null);

    const start = async () => {
        setError("");
        setPreviewUrl(null);
        setCapturedBlob(null);
        try {
            const s = await navigator.mediaDevices.getUserMedia({
                video: { facingMode: "user", width: { ideal: 720 }, height: { ideal: 960 } },
                audio: false,
            });
            streamRef.current = s;
            if (videoRef.current) {
                videoRef.current.srcObject = s;
                await videoRef.current.play();
                setReady(true);
            }
        } catch (e) {
            setError("Camera permission denied. Please enable camera access and try again.");
        }
    };

    useEffect(() => {
        if (!existingUrl) start();
        return () => {
            if (streamRef.current) {
                streamRef.current.getTracks().forEach(t => t.stop());
                streamRef.current = null;
            }
        };
        // eslint-disable-next-line
    }, []);

    const snap = () => {
        const video = videoRef.current;
        const canvas = canvasRef.current;
        if (!video || !canvas) return;
        const w = video.videoWidth;
        const h = video.videoHeight;
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(video, 0, 0, w, h);
        canvas.toBlob((blob) => {
            if (!blob) return;
            setCapturedBlob(blob);
            setPreviewUrl(URL.createObjectURL(blob));
            if (streamRef.current) {
                streamRef.current.getTracks().forEach(t => t.stop());
                streamRef.current = null;
            }
        }, "image/jpeg", 0.9);
    };

    const retake = () => {
        if (previewUrl && previewUrl.startsWith("blob:")) URL.revokeObjectURL(previewUrl);
        start();
    };

    const confirm = () => {
        if (!capturedBlob) return;
        onCapture(capturedBlob);
    };

    return (
        <div className="flex-1 flex flex-col bg-cream" data-testid="live-photo-screen">
            <div className="px-6 pt-6 pb-2 flex items-center justify-between">
                <button
                    data-testid="live-photo-cancel"
                    onClick={onCancel}
                    className="tap-target text-[11px] tracking-[0.28em] uppercase text-ink-muted"
                >
                    ← Back
                </button>
                <span className="text-[10px] tracking-[0.32em] uppercase text-ink-muted">
                    Live photo
                </span>
            </div>

            <div className="px-7 pt-3">
                <Eyebrow>Real time</Eyebrow>
                <h2 className="mt-2 font-display text-3xl text-burgundy leading-tight">
                    <span className="italic">Smile</span> for us.
                </h2>
                <p className="mt-1 text-sm text-ink-muted">
                    A single live photo, taken now. No filters, no gallery — that's the point.
                </p>
            </div>

            <div className="flex-1 px-6 py-6 flex flex-col items-center justify-center">
                <div className="w-full aspect-[3/4] bg-burgundy/5 border border-burgundy/15 overflow-hidden relative" data-testid="live-photo-stage">
                    {previewUrl ? (
                        <img src={previewUrl} alt="Captured" className="w-full h-full object-cover" data-testid="live-photo-preview" />
                    ) : (
                        <video
                            ref={videoRef}
                            playsInline
                            muted
                            className="w-full h-full object-cover"
                            data-testid="live-photo-video"
                        />
                    )}
                    {!previewUrl && ready && (
                        <div className="absolute bottom-3 left-3 right-3 text-center text-[10px] tracking-[0.28em] uppercase text-cream bg-burgundy/80 py-1.5">
                            Ready
                        </div>
                    )}
                </div>
                <canvas ref={canvasRef} className="hidden" />
                {error && <div data-testid="live-photo-error" className="mt-3 text-sm text-burgundy">{error}</div>}
            </div>

            <div className="px-6 pb-8 space-y-2.5">
                {!previewUrl ? (
                    <Btn
                        data-testid="live-photo-snap"
                        onClick={snap}
                        disabled={!ready}
                        className={!ready ? "opacity-40 cursor-not-allowed" : ""}
                    >
                        Take photo
                    </Btn>
                ) : (
                    <>
                        <Btn data-testid="live-photo-confirm" onClick={confirm}>
                            Use this photo
                        </Btn>
                        <Btn data-testid="live-photo-retake" variant="secondary" onClick={retake}>
                            Retake
                        </Btn>
                    </>
                )}
            </div>
        </div>
    );
}
